import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import './Home.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';
import {
  faSpinner,
  faHeartPulse, faShieldHalved, faStethoscope,
  faDumbbell, faBaby, faHandHoldingHeart, faBrain, faVenus,
  faCalendarCheck, faCapsules, faWeightScale, faHeartbeat,
  faLungs, faDroplet, faPhoneVolume, faFlask,
  faArrowRight, faClipboardCheck,
  faUser, faBell, faPlus, faClock, faClipboardList,
  faUpload, faListCheck, faSearch, faLink, faFileMedical,
  faPersonWalking, faFire, faMoon, faStar, faBolt,
  faWaveSquare, faWind, faBatteryFull, faRoute, faGift, faTags,
  faBullhorn, faUserDoctor, faSpa, faHeadset, faTimes, faFaceSmile, faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import healthRecordsService from '../../services/healthRecordsService';
import healthSummaryService from '../../services/healthSummaryService';
import reminderService, { Reminder } from '../../services/reminderService';
import taskService from '../../services/taskService';
import wearableService from '../../services/wearableService';
import AnnouncementCard from '../../components/AnnouncementCard/AnnouncementCard';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { WearableDataPoint } from '../../types';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import { useTranslation } from 'react-i18next';

interface MyAnnouncement {
  id: number;
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  is_read: boolean;
  created_at: string;
}

interface VitalItem {
  icon: IconDefinition;
  color: string;
  value: string;
  label: string;
  source?: 'manual' | 'wearable';
  bmiCategory?: { label: string; color: string };
}

const getReminderTab = (r: Reminder): 'upcoming' | 'today' | 'completed' => {
  if (r.is_completed) return 'completed';
  const dt = new Date(r.reminder_datetime);
  const now = new Date();
  if (dt.toDateString() === now.toDateString()) return 'today';
  if (dt < now) return 'today';
  return 'upcoming';
};

const getBmiCategory = (category: string): { label: string; color: string } => {
  const map: Record<string, { label: string; color: string }> = {
    underweight: { label: 'Underweight', color: '#3b82f6' },
    normal: { label: 'Normal', color: '#10b981' },
    overweight: { label: 'Overweight', color: '#f59e0b' },
    obese: { label: 'Obese', color: '#f97316' },
    severely_obese: { label: 'Severely Obese', color: '#ef4444' },
  };
  return map[category] || { label: 'Unknown', color: '#64748b' };
};  

const Home: React.FC = () => {
  const { activeProfile } = useProfile();
  const careProgramRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation(['home', 'common']);

  // BMI category label translations (getBmiCategory returns English keys)
  const bmiCategoryTranslations: Record<string, string> = useMemo(() => ({
    'Underweight': t('home:vitals.bmiCategories.underweight'),
    'Normal': t('home:vitals.bmiCategories.normal'),
    'Overweight': t('home:vitals.bmiCategories.overweight'),
    'Obese': t('home:vitals.bmiCategories.obese'),
    'Severely Obese': t('home:vitals.bmiCategories.severelyObese'),
  }), [t]);

  // Corporate service assignments (free/paid + visibility). The context already
  // scopes the list to the employee's corporate, so a service the corporate has
  // not been granted simply never appears here.
  const { ready: serviceAccessReady, canAccess, getAssignment: getServiceAssignment } = useServiceAccess();

  const showVitals = serviceAccessReady && canAccess('health-records');
  const showWearableActivity = serviceAccessReady && canAccess('wearable-devices');

  // Default vitals with FA icons
  const defaultVitals: VitalItem[] = [
    { icon: faWeightScale, color: '#06b6d4', value: '--', label: t('home:vitals.bmi') },
    { icon: faHeartPulse, color: '#f43f5e', value: '--', label: t('home:vitals.bloodPressure') },
    { icon: faHeartbeat, color: '#8b5cf6', value: '--', label: t('home:vitals.heartRate') },
    { icon: faLungs, color: '#10b981', value: '--', label: t('home:vitals.o2Saturation') },
    { icon: faDroplet, color: '#f59e0b', value: '--', label: t('home:vitals.glucose') },
  ];

  const [vitals, setVitals] = useState<VitalItem[]>(defaultVitals);
  const [vitalsLoading, setVitalsLoading] = useState(true);

  // Wearable activity state
  const [wearableMetrics, setWearableMetrics] = useState<WearableDataPoint[]>([]);
  const [wearableLoading, setWearableLoading] = useState(true);

  const [healthScore, setHealthScore] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- read by the commented-out health score widget below
  const [healthScoreLabel, setHealthScoreLabel] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- read by the commented-out health insights button below
  const [healthSummaryLoading, setHealthSummaryLoading] = useState(true);

  // Wearable metric display config
  const wearableMetricConfig: Record<string, { label: string; icon: IconDefinition; color: string; formatValue: (v: number) => string }> = {
    heart_rate:         { label: t('home:wearable.heartRate'),   icon: faHeartPulse,     color: '#f43f5e',  formatValue: (v) => `${v} BPM` },
    blood_oxygen:       { label: t('home:wearable.bloodOxygen'), icon: faLungs,          color: '#06b6d4',  formatValue: (v) => `${v}%` },
    steps:              { label: t('home:wearable.steps'),       icon: faPersonWalking,  color: '#10b981',  formatValue: (v) => v.toLocaleString() },
    calories:           { label: t('home:wearable.calories'),    icon: faFire,           color: '#f59e0b',  formatValue: (v) => `${v.toLocaleString()} kcal` },
    sleep_duration:     { label: t('home:wearable.sleep'),       icon: faMoon,           color: '#6366f1',  formatValue: (v) => { const h = Math.floor(v / 60); const m = Math.round(v % 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; } },
    sleep_score:        { label: t('home:wearable.sleepScore'),  icon: faStar,           color: '#a855f7',  formatValue: (v) => `${v}` },
    active_minutes:     { label: t('home:wearable.activeMin'),   icon: faBolt,           color: '#ec4899',  formatValue: (v) => `${v} min` },
    distance:           { label: t('home:wearable.distance'),    icon: faRoute,          color: '#8b5cf6',  formatValue: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
    resting_heart_rate: { label: t('home:wearable.restingHR'),   icon: faHeartbeat,      color: '#e11d48',  formatValue: (v) => `${v} BPM` },
    hrv:                { label: t('home:wearable.hrv'),         icon: faWaveSquare,     color: '#7c3aed',  formatValue: (v) => `${v} ms` },
    stress_level:       { label: t('home:wearable.stress'),      icon: faBrain,          color: '#ef4444',  formatValue: (v) => `${v}` },
    body_battery:       { label: t('home:wearable.bodyBattery'), icon: faBatteryFull,    color: '#22c55e',  formatValue: (v) => `${v}%` },
    respiratory_rate:   { label: t('home:wearable.respRate'),    icon: faWind,           color: '#0ea5e9',  formatValue: (v) => `${v} brpm` },
    glucose:            { label: t('home:wearable.glucose'),     icon: faDroplet,        color: '#eab308',  formatValue: (v) => `${v} mg/dL` },
  };

  const fetchVitals = useCallback(async () => {
    setVitalsLoading(true);
    try {
      const [vitalsResult, wearableResult] = await Promise.allSettled([
        healthRecordsService.getAllLatestVitals(),
        wearableService.getLatestDataPoints(),
      ]);

      const allVitals = vitalsResult.status === 'fulfilled' ? vitalsResult.value : {
        height: null, weight: null, bmi: null, bloodPressure: null,
        heartRate: null, oxygenSaturation: null, glucose: null,
      };

      // Build a lookup of wearable metrics by type
      const wData = wearableResult.status === 'fulfilled' ? wearableResult.value : [];
      const wMap: Record<string, WearableDataPoint> = {};
      wData.forEach(p => { wMap[p.metric_type] = p; });

      // Helper: check if wearable reading is fresher than manual
      const preferWearable = (manualDate: string | undefined, wMetric: string): boolean => {
        const w = wMap[wMetric];
        if (!w) return false;
        if (!manualDate) return true;
        return new Date(w.recorded_at) > new Date(manualDate);
      };

      // Heart Rate: prefer wearable if fresher
      const manualHr = allVitals.heartRate;
      const wHr = wMap['heart_rate'];
      let hrValue = '--';
      let hrSource: 'manual' | 'wearable' = 'manual';
      if (wHr && preferWearable(manualHr?.created_at, 'heart_rate')) {
        hrValue = `${wHr.value} BPM`;
        hrSource = 'wearable';
      } else if (manualHr) {
        hrValue = `${manualHr.value} BPM`;
      }

      // O2 Saturation: prefer wearable if fresher
      const manualO2 = allVitals.oxygenSaturation;
      const wO2 = wMap['blood_oxygen'];
      let o2Value = '--';
      let o2Source: 'manual' | 'wearable' = 'manual';
      if (wO2 && preferWearable(manualO2?.created_at, 'blood_oxygen')) {
        o2Value = `${wO2.value}%`;
        o2Source = 'wearable';
      } else if (manualO2) {
        o2Value = `${manualO2.value}%`;
      }

      // Glucose: prefer wearable if fresher
      const manualGlucose = allVitals.glucose;
      const wGlucose = wMap['glucose'];
      let glucoseValue = '--';
      let glucoseSource: 'manual' | 'wearable' = 'manual';
      if (wGlucose && preferWearable(manualGlucose?.created_at, 'glucose')) {
        glucoseValue = `${wGlucose.value} mg/dL`;
        glucoseSource = 'wearable';
      } else if (manualGlucose) {
        glucoseValue = `${manualGlucose.value} mg/dL`;
      }

      setVitals([
        {
          icon: faWeightScale, color: '#06b6d4',
          value: allVitals.bmi
            ? (bmiCategoryTranslations[allVitals.bmi.category_label || ''] || allVitals.bmi.category_label || '--')
            : '--',
          label: t('home:vitals.bmi'),
          source: 'manual',
          bmiCategory: allVitals.bmi
            ? getBmiCategory(allVitals.bmi.category || '') : undefined,
        },
        {
          icon: faHeartPulse, color: '#f43f5e',
          value: allVitals.bloodPressure ? `${allVitals.bloodPressure.systolic}/${allVitals.bloodPressure.diastolic}` : '--',
          label: t('home:vitals.bloodPressure'),
          source: 'manual',
        },
        {
          icon: faHeartbeat, color: '#8b5cf6',
          value: hrValue,
          label: t('home:vitals.heartRate'),
          source: hrSource,
        },
        {
          icon: faLungs, color: '#10b981',
          value: o2Value,
          label: t('home:vitals.o2Saturation'),
          source: o2Source,
        },
        {
          icon: faDroplet, color: '#f59e0b',
          value: glucoseValue,
          label: t('home:vitals.glucose'),
          source: glucoseSource,
        },
      ]);
    } catch (err) {
      console.error('Error fetching vitals:', err);
    } finally {
      setVitalsLoading(false);
    }
  }, [bmiCategoryTranslations, t]);

  const fetchWearableMetrics = useCallback(async () => {
    setWearableLoading(true);
    try {
      const data = await wearableService.getLatestDataPoints();
      setWearableMetrics(data);
    } catch (err) {
      console.error('Error fetching wearable metrics:', err);
    } finally {
      setWearableLoading(false);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the commented-out health score widget below
  const getScoreGradient = (score: number) => {
      if (score >= 80) {
          return "linear-gradient(135deg,#22c55e 0%,#16a34a 100%)";
      }

      if (score >= 60) {
          return "linear-gradient(135deg,#f59e0b 0%,#ea580c 100%)";
      }

      return "linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the commented-out health score widget below
  const getScoreColor = (score: number) => {
      if (score >= 80) return "#22c55e";
      if (score >= 60) return "#f59e0b";
      return "#f87171";
  }; 

  const fetchHealthSummary = useCallback(async () => {
      try {
          setHealthSummaryLoading(true);

          const data = await healthSummaryService.getLatestSummary();

          setHealthScore(data.health_score);
          setHealthScoreLabel(data.health_score_label);
      } catch {
          setHealthScore(null);
          setHealthScoreLabel("Not Available");
      } finally {
          setHealthSummaryLoading(false);
      }
  }, []);

  useEffect(() => {
      if (showVitals) fetchVitals();
      if (showWearableActivity) fetchWearableMetrics();
      if (serviceAccessReady) fetchHealthSummary();
  }, [fetchVitals, fetchWearableMetrics, fetchHealthSummary, showVitals, showWearableActivity, serviceAccessReady, activeProfile]);

  useEffect(() => {
    const handleVitalsUpdated = () => {
      if (showVitals) fetchVitals();
    };

    window.addEventListener('vitals-updated', handleVitalsUpdated);
    return () => {
      window.removeEventListener('vitals-updated', handleVitalsUpdated);
    };
  }, [fetchVitals, showVitals]);

  // Disease risk profile
  const [riskProfile, setRiskProfile] = useState<any>(null);
  const [riskBannerDismissed, setRiskBannerDismissed] = useState(() => {
    return sessionStorage.getItem('riskBannerDismissed') === 'true';
  });

  useEffect(() => {
    if (!canAccess('disease-prediction')) {
      setRiskProfile(null);
      return;
    }
    const fetchRisk = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.latest);
        if (res.data.has_prediction) setRiskProfile(res.data);
      } catch { /* silently fail */ }
    };
    fetchRisk();
  }, [activeProfile, canAccess]);

  // Upcoming wellness events
  interface UpcomingWellnessEvent {
    id: number;
    title: string;
    event_date: string;
    category: string;
    mode: string;
    expert_name?: string;
    is_registered: boolean;
    registration_count: number;
    max_participants?: number;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- read by the commented-out upcoming events section below
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingWellnessEvent[]>([]);

  useEffect(() => {
    const fetchUpcomingEvents = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.wellness.upcomingConfirmed);
        setUpcomingEvents(Array.isArray(res.data) ? res.data : []);
      } catch {
        // silently fail
      }
    };
    fetchUpcomingEvents();
  }, [activeProfile]);

  // Employee announcements
  const [myAnnouncements, setMyAnnouncements] = useState<MyAnnouncement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      setAnnouncementsLoading(true);
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.myAnnouncements);
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setMyAnnouncements(data);
      } catch {
        // silently fail — announcements are non-critical
      } finally {
        setAnnouncementsLoading(false);
      }
    };
    fetchAnnouncements();
  }, [activeProfile]);

  const [markingReadId, setMarkingReadId] = useState<number | null>(null);

  const handleMarkAsRead = async (id: number) => {
    setMarkingReadId(id);
    try {
      await axiosInstance.post(API_ENDPOINTS.markAnnouncementRead(id));
      setMyAnnouncements((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_read: true } : a))
      );
    } catch (err) {
      console.error('Failed to mark announcement as read:', err);
    } finally {
      setMarkingReadId(null);
    }
  };

  const announcementPriorityConfig: Record<string, { color: string; label: string }> = {
    low: { color: '#64748b', label: t('home:announcements.priority.low') },
    normal: { color: '#2563eb', label: t('home:announcements.priority.normal') },
    high: { color: '#ea580c', label: t('home:announcements.priority.high') },
    urgent: { color: '#dc2626', label: t('home:announcements.priority.urgent') },
  };

  // Reminders, Tasks & Support Tickets
  const [activeReminders, setUpcomingReminders] = useState<Reminder[]>([]);
  const [tasks, setTasks] = useState<Reminder[]>([]);
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  const categoryConfig: Record<string, { icon: IconDefinition; color: string; label: string }> = {
    appointment: { icon: faCalendarCheck, color: '#8b5cf6', label: 'Appointment' },
    lab_test: { icon: faFlask, color: '#10b981', label: 'Lab Test' },
    follow_up: { icon: faPhoneVolume, color: '#06b6d4', label: 'Follow-up' },
    medication: { icon: faCapsules, color: '#f59e0b', label: 'Medication' },
    personal: { icon: faUser, color: '#f43f5e', label: 'Personal' },
    other: { icon: faBell, color: '#64748b', label: 'Other' },
  };

  const getDependantId = React.useCallback((): number | undefined => {
    if (activeProfile && activeProfile.type !== 'self' && typeof activeProfile.id === 'number') {
      return activeProfile.id;
    }
    return undefined;
  }, [activeProfile]);

  const fetchUpcomingReminders = React.useCallback(async () => {
    setRemindersLoading(true);
    setTasksLoading(true);
    try {
      const [reminders, fetchedTasks] = await Promise.all([
        reminderService.getReminders(getDependantId()),
        taskService.getTasks({ is_completed: false }),
      ]);
      setUpcomingReminders(reminders.filter((r: Reminder) => !r.is_completed));
      setTasks(fetchedTasks);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setRemindersLoading(false);
      setTasksLoading(false);
    }
  }, [getDependantId]);

  const fetchSupportTickets = React.useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.support.tickets, { params: { status: 'open', page_size: 10 } });
      const data = res.data;
      setSupportTickets(data.results || data || []);
    } catch {
      // Non-critical
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingReminders();
    fetchSupportTickets();
  }, [fetchUpcomingReminders, fetchSupportTickets, activeProfile]);

  useEffect(() => {
    const handler = () => fetchUpcomingReminders();
    window.addEventListener('reminders-updated', handler);
    return () => window.removeEventListener('reminders-updated', handler);
  }, [fetchUpcomingReminders]);

  const formatReminderDate = (datetime: string) => {
    const dt = new Date(datetime);
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
      + ' ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const statusLabelMap: Record<string, { label: string; color: string }> = {
    new: { label: 'New', color: '#3b82f6' },
    assigned: { label: 'Assigned', color: '#06b6d4' },
    in_progress: { label: 'In Progress', color: '#6366f1' },
    pending_vendor: { label: 'Pending', color: '#f59e0b' },
    scheduled: { label: 'Scheduled', color: '#8b5cf6' },
    completed: { label: 'Resolved', color: '#10b981' },
    cancelled: { label: 'Cancelled', color: '#ef4444' },
  };

  useEffect(() => {
    if (location.hash === '#care-program' && careProgramRef.current) {
      careProgramRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);

  // Services data — serviceSlug maps to PlatformService.slug for free/paid gating
  interface QuickAction { label: string; icon: IconDefinition; route: string }
  interface ServiceItem {
    title: string; description: string; img: string; video?: string; comingSoon: boolean;
    icon: IconDefinition; color: string; quickActions?: QuickAction[];
    serviceSlug?: string;
    videoPosition?: string;
    videoFit?: 'cover' | 'contain' | 'fill';
  }

  const services: ServiceItem[] = useMemo(() => [
    { title: t('home:services.aiFamilyDoctor'), serviceSlug: 'ai-family-doctor', description: t('home:services.aiFamilyDoctorDesc'), img: '/Consultation.jpeg', comingSoon: false, icon: faUserDoctor, color: '#8b5cf6', quickActions: [
      { label: t('home:services.consultNow'), icon: faUserDoctor, route: '/ai-family-doctor' },
    ]},
    { title: t('home:services.healthRecords'), serviceSlug: 'health-records', description: t('home:services.healthRecordsDesc'), img: '/Medical records.jpeg', video: '/images/Health_Records.mp4', comingSoon: false, icon: faStethoscope, color: '#06b6d4', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: t('home:services.viewVitals'), icon: faHeartPulse, route: '/health-records' },
      { label: t('home:services.uploadRecord'), icon: faUpload, route: '/health-records?action=add' },
    ]},
    { title: t('home:services.healthAssessment'), serviceSlug: 'health-assessment', description: t('home:services.healthAssessmentDesc'), img: '/Health Assessment.jpeg', video: '/images/Health_assessment.mp4', comingSoon: false, icon: faHeartPulse, color: '#f43f5e', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: t('home:services.startAssessment'), icon: faClipboardCheck, route: '/health-assessment' },
    ]},
    { title: t('home:services.healthInsights'), serviceSlug: 'health-insights', description: t('home:services.healthInsightsDesc'), img: '/Health Assessment.jpeg', video: '/images/Health_insights.mp4', comingSoon: false, icon: faFileMedical, color: '#0891b2', videoPosition: 'center 40%', videoFit: 'cover', quickActions: [
      { label: t('home:services.viewInsights'), icon: faFileMedical, route: '/health-summary' },
    ]},
    { title: t('home:services.medicineReminder'), serviceSlug: 'medicine-reminder', description: t('home:services.medicineReminderDesc'), img: '/medicine.jpeg', video: '/images/Medicine_reminder.mp4', comingSoon: false, icon: faCapsules, color: '#10b981', videoPosition: 'center 35%', videoFit: 'cover', quickActions: [
      { label: t('home:services.addReminder'), icon: faPlus, route: '/medicinereminder?tab=add' },
      { label: t('common:buttons.viewAll'), icon: faListCheck, route: '/medicinereminder' },
    ]},
    { title: t('home:services.symptomsTracker'), serviceSlug: 'symptoms-tracker', description: t('home:services.symptomsTrackerDesc'), img: '/Symptoms Checker.jpeg', video: '/images/Symptom_tracker.mp4', comingSoon: false, icon: faClipboardList, color: '#ef4444', videoPosition: 'center 35%', videoFit: 'cover', quickActions: [
      { label: t('home:services.logSymptom'), icon: faPlus, route: '/symptoms-checker?tab=add' },
      { label: t('home:services.viewConditions'), icon: faSearch, route: '/symptoms-checker?tab=conditions' },
    ]},
    { title: t('home:services.smartReports'), serviceSlug: 'smart-reports', description: t('home:services.smartReportsDesc'), img: '/Smart Reports.jpeg', video: '/images/Smart_report.mp4', comingSoon: false, icon: faFlask, color: '#8b5cf6', videoPosition: 'center 40%', videoFit: 'cover', quickActions: [
      { label: t('home:services.uploadReport'), icon: faUpload, route: '/smart-reports?tab=upload' },
      { label: t('home:services.myReports'), icon: faClipboardList, route: '/smart-reports' },
    ]},
    { title: t('home:services.insuranceManagement'), serviceSlug: 'insurance-management', description: t('home:services.insuranceManagementDesc'), img: '/Insurance Records.jpeg', video: '/images/Insurance_Management.mp4', comingSoon: false, icon: faShieldHalved, color: '#8b5cf6', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: t('home:services.addPolicy'), icon: faPlus, route: '/insurance-records/add' },
      { label: t('home:services.viewPolicies'), icon: faShieldHalved, route: '/insurance-record' },
    ]},
    { title: t('home:services.wearableDevices'), serviceSlug: 'wearable-devices', description: t('home:services.wearableDevicesDesc'), img: '/Wearables.svg', video: '/images/Werables.mp4', comingSoon: false, icon: faClock, color: '#06b6d4', videoPosition: 'center 23%', videoFit: 'cover', quickActions: [
      { label: t('home:services.connectDevice'), icon: faLink, route: '/wearable-devices' },
      { label: t('home:services.viewData'), icon: faHeartbeat, route: '/wearable-devices' },
    ]},
    { title: t('home:services.gymServices'), serviceSlug: 'gym-services', description: t('home:services.gymServicesDesc'), img: '/GYM.jpeg', video: '/images/gym.mp4', comingSoon: false, icon: faDumbbell, color: '#f59e0b', videoPosition: 'center 20%', videoFit: 'cover', quickActions: [
      { label: t('home:services.findGym'), icon: faSearch, route: '/gymservices' },
    ]},
    { title: t('home:services.teleConsultation'), serviceSlug: 'tele-consultation', description: t('home:services.teleConsultationDesc'), img: '/Health Assessment.jpeg', video: '/images/Teleconsultation.mp4', comingSoon: false, icon: faUserDoctor, color: '#0e7490', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: 'Book a Consultation', icon: faStethoscope, route: '/teleconsultation' },
      { label: 'My Appointments', icon: faUserDoctor, route: '/teleconsultation?tab=appointments' },
    ]},
    { title: t('home:services.womensHealth'), serviceSlug: 'women-health', description: t('home:services.womensHealthDesc'), img: '/women_health.jpg', video: '/images/Womens_health.mp4', comingSoon: false, icon: faVenus, color: '#f472b6', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: t('home:services.openDashboard'), icon: faVenus, route: '/womens-health' },
    ]},
    { title: t('home:services.childDev'), serviceSlug: 'child-development', description: t('home:services.childDevDesc'), img: '/vaccination_baby.jpg', video: '/images/child_care.mp4', comingSoon: false, icon: faBaby, color: '#ec4899', videoPosition: 'center 30%', videoFit: 'cover', quickActions: [
      { label: t('home:services.viewGrowth'), icon: faBaby, route: '/child-development' },
    ]},
    { title: t('home:services.elderlyCare'), serviceSlug: 'elderly-care', description: t('home:services.elderlyCareDesc'), img: '/Elderly Care.jpeg', video: '/images/Elderly_care.mp4', comingSoon: false, icon: faHandHoldingHeart, color: '#0891b2', videoPosition: 'center 15%', videoFit: 'cover', quickActions: [
      { label: t('home:services.viewServices'), icon: faHandHoldingHeart, route: '/elderly-care' },
    ]},
    { title: t('home:services.groupMediclaim'), serviceSlug: 'group-mediclaim', description: t('home:services.groupMediclaimDesc'), img: '/Insurance Records.jpeg', video: '/images/Group_Medi.mp4', comingSoon: false, icon: faShieldHalved, color: '#0d9488', videoPosition: 'center 35%', videoFit: 'cover', quickActions: [
      { label: t('home:services.viewPolicies'), icon: faShieldHalved, route: '/my-mediclaim' },
    ]},
    { title: t('home:services.sponsoredPackages'), serviceSlug: 'sponsored-packages', description: t('home:services.sponsoredPackagesDesc'), img: '/Sponsored Packages.jpeg', video: '/images/Sponsored_packages.mp4', comingSoon: false, icon: faGift, videoPosition: 'center 35%', videoFit: 'cover', color: '#06b6d4', quickActions: [
      { label: t('home:services.browsePackages'), icon: faGift, route: '/sponsored-packages' },
    ]},
    { title: t('home:services.mentalWellness'), serviceSlug: 'mental-wellness', description: t('home:services.mentalWellnessDesc'), img: '/Mental Wellness.jpeg', video: '/images/Mental_Wellness.mp4', comingSoon: false, icon: faBrain, color: '#7c3aed', videoPosition: 'center 40%', videoFit: 'cover', quickActions: [
      { label: 'Explore', icon: faBrain, route: '/mental-wellness' },
      { label: 'Mood Check-in', icon: faFaceSmile, route: '/mental-wellness/mood-checkin' },
      { label: 'Breathing Exercise', icon: faWind, route: '/mental-wellness/breathing' },
    ]},
  ], [t]);

  const visibleServices = useMemo(
    () => services.filter((s) => canAccess(s.serviceSlug)),
    [services, canAccess],
  );

  const priorityColors: Record<string, string> = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#f43f5e',
  };

  return (
    <div className="home-page">

      {/* ============ WELCOME BANNER ============ */}
      <section className="home-welcome-banner">
        <Container>
          <div className="welcome-layout">

            <div className="welcome-text">
              <h1 className="welcome-title">
                {t('home:greeting', { name: user?.name?.split(' ')[0] || '' })}
              </h1>

              <p className="welcome-subtitle">
                {t('home:greetingSubtitle')}
              </p>
            </div>

          <div
            className={`welcome-actions-wrapper ${
              healthScore !== null ? "has-score" : "no-score"
            }`}
          >
            <div className="welcome-actions">
              {canAccess('health-records') && (
                <button
                  className="welcome-action-btn primary"
                  onClick={() => navigate('/health-records')}
                >
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  {t('home:quickActions.healthRecords')}
                </button>
              )}

              {canAccess('health-assessment') && (
                <button
                  className="welcome-action-btn secondary"
                  onClick={() => navigate('/health-assessment')}
                >
                  <FontAwesomeIcon icon={faHeartPulse} />
                  {t('home:quickActions.healthAssessment')}
                </button>
              )}

              <button
                className="welcome-action-btn secondary"
                onClick={() => navigate('/profile', { state: { tab: 'family' } })}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                {t('home:quickActions.addDependant')}
              </button>

              {/* Show Health Insights button only if no score */}
              {/* {(!healthSummaryLoading && healthScore === null) && (
                <button
                  className="welcome-action-btn tertiary"
                  onClick={() => navigate('/health-summary')}
                >
                  <FontAwesomeIcon icon={faChartLine} />
                  Health Insights
                </button>
              )} */}
            </div>

            {/* Replace Health Insights with Health Score */}
            {/* {!healthSummaryLoading && healthScore !== null && (
              <div
                className="welcome-health-score"
                onClick={() => navigate('/health-summary')}
              >
                <div
                  className="welcome-health-score-circle"
                  style={{
                    background: getScoreGradient(healthScore),
                  }}
                >
                  {healthScore}
                </div>

                <div className="welcome-health-score-info">
                  <span className="welcome-health-score-title">
                    Health Score
                  </span>

                  <span
                    className="welcome-health-score-label"
                    style={{
                      color: getScoreColor(healthScore),
                    }}
                  >
                    {healthScoreLabel}
                  </span>
                </div>
              </div>
            )} */}
          </div>
          </div>
        </Container>
      </section>

      {/* ============ VITALS STATS ROW ============ */}
      {showVitals && (
      <section className="home-vitals-section">
        <Container>
          <div className="vitals-stats-row">
            {vitals.map((item, idx) => (
              <div
                className={`vital-stat-card${vitalsLoading ? ' loading' : ''}`}
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/health-records')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/health-records');
                  }
                }}
                aria-label={item.label}
              >
                <div className="vital-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="vital-stat-info">
                  <span
                    className="vital-stat-value"
                    style={item.bmiCategory ? { color: item.bmiCategory.color } : undefined}
                  >
                    {item.value}
                  </span>
                  <span className="vital-stat-label">
                    {item.label}
                    {item.source === 'wearable' && (
                      <span className="vital-wearable-badge">
                        <FontAwesomeIcon icon={faClock} /> {t('home:vitals.source.wearable')}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
      )}

      {/* ============ WEARABLE ACTIVITY ============ */}
      {(showWearableActivity && !wearableLoading && wearableMetrics.length > 0) && (
        <section className="home-wearable-section">
          <Container>
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  <FontAwesomeIcon icon={faClock} className="section-title-icon" />
                  {t('home:wearable.title')}
                </h2>
                <p className="section-subtitle">{t('home:wearable.subtitle')}</p>
              </div>
              <button className="events-view-all-btn" onClick={() => navigate('/wearable-devices')}>
                {t('common:buttons.viewAll')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
            <div className="wearable-metrics-row">
              {wearableMetrics.slice(0, 8).map((point) => {
                const config = wearableMetricConfig[point.metric_type];
                if (!config) return null;
                return (
                  <div className="wearable-metric-card" key={point.id}>
                    <div className="wearable-metric-icon" style={{ background: `${config.color}12`, color: config.color }}>
                      <FontAwesomeIcon icon={config.icon} />
                    </div>
                    <div className="wearable-metric-info">
                      <span className="wearable-metric-value">{config.formatValue(point.value)}</span>
                      <span className="wearable-metric-label">{config.label}</span>
                    </div>
                    <span className="wearable-metric-provider">{point.provider_name}</span>
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      )}

      {/* ============ DISEASE RISK ALERT BANNER ============ */}
      {canAccess('disease-prediction') && !riskBannerDismissed && (
      <section className="home-risk-banner-section">
        <Container>
          {riskProfile ? (() => {
            const cat = riskProfile.overall_risk_category;
            const gradientMap: Record<string, string> = {
              low: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              moderate: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              high: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
              critical: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            };
            const colorMap: Record<string, string> = { low: '#10b981', moderate: '#f59e0b', high: '#f97316', critical: '#ef4444' };
            const borderColorMap: Record<string, string> = { low: '#a7f3d0', moderate: '#fde68a', high: '#fed7aa', critical: '#fecaca' };
            const accentColor = colorMap[cat] || '#f59e0b';
            const elevatedDiseases = riskProfile.disease_details
              ? Object.entries(riskProfile.disease_details)
                  .filter(([, d]: [string, any]) => d.score > 30)
                  .sort(([, a]: [string, any], [, b]: [string, any]) => b.score - a.score)
                  .slice(0, 3)
              : [];
            return (
              <div
                className="home-risk-banner"
                onClick={() => navigate('/disease-prediction')}
                style={{ background: gradientMap[cat] || gradientMap.moderate, borderColor: borderColorMap[cat] || borderColorMap.moderate }}
              >
                <div className="home-risk-banner-score" style={{ background: accentColor }}>
                  <span className="home-risk-banner-score-num">{riskProfile.overall_risk_score}</span>
                </div>
                <div className="home-risk-banner-info">
                  <span className="home-risk-banner-title">{t('home:riskBanner.title')}</span>
                  <span className="home-risk-banner-cat" style={{ color: accentColor }}>
                    {t('home:riskBanner.riskLevel', { level: cat.charAt(0).toUpperCase() + cat.slice(1) })}
                  </span>
                </div>
                {elevatedDiseases.length > 0 && (
                  <div className="home-risk-banner-chips">
                    {elevatedDiseases.map(([name, d]: [string, any]) => (
                      <span key={name} className="home-risk-banner-chip" style={{ background: `${colorMap[d.category] || '#f59e0b'}18`, color: colorMap[d.category] || '#f59e0b', borderColor: `${colorMap[d.category] || '#f59e0b'}40` }}>
                        {name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: {d.score}
                      </span>
                    ))}
                  </div>
                )}
                <div className="home-risk-banner-action">
                  <span>{t('home:riskBanner.viewDetails')}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <button
                  className="home-risk-banner-close"
                  onClick={(e) => { e.stopPropagation(); setRiskBannerDismissed(true); sessionStorage.setItem('riskBannerDismissed', 'true'); }}
                  aria-label="Dismiss risk banner"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            );
          })() : (
            <div className="home-risk-banner home-risk-banner--empty" onClick={() => navigate('/disease-prediction')}>
              <div className="home-risk-banner-score home-risk-banner-score--empty">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div className="home-risk-banner-info">
                <span className="home-risk-banner-title">{t('home:riskBanner.titleEmpty')}</span>
                <span className="home-risk-banner-cat" style={{ color: '#64748b' }}>{t('home:riskBanner.getPersonalized')}</span>
              </div>
              <div className="home-risk-banner-action">
                <span>{t('home:riskBanner.getStarted')}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              <button
                className="home-risk-banner-close"
                onClick={(e) => { e.stopPropagation(); setRiskBannerDismissed(true); sessionStorage.setItem('riskBannerDismissed', 'true'); }}
                aria-label="Dismiss risk banner"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}
        </Container>
      </section>
      )}

      {/* ============ UPCOMING WELLNESS EVENTS ============ */}
      {/* {upcomingEvents.length > 0 && (
        <section className="home-wellness-banner-section">
          <Container>
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  <FontAwesomeIcon icon={faSpa} className="section-title-icon" />
                  {t('home:upcomingEvents.title')}
                </h2>
                <p className="section-subtitle">{t('home:upcomingEvents.subtitle')}</p>
              </div>
              <button className="events-view-all-btn" onClick={() => navigate('/wellness-calendar')}>
                {t('common:buttons.viewAll')} <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
            <div className="wellness-banner-grid">
              {upcomingEvents.slice(0, 3).map((evt) => {
                const isFull = evt.max_participants ? evt.registration_count >= evt.max_participants : false;
                return (
                  <div className="wellness-banner-card" key={evt.id} onClick={() =>
                    navigate('/wellness-calendar', {
                      state: {
                        eventDate: evt.event_date,
                      },    
                    })
                  }>
                    <div className="wellness-banner-card-top">
                      <span className="wellness-banner-date">
                        <FontAwesomeIcon icon={faCalendarCheck} />
                        {new Date(evt.event_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      <span className={`wellness-banner-mode wellness-banner-mode--${evt.mode}`}>
                        {evt.mode === 'online' ? t('home:upcomingEvents.online') : evt.mode === 'onsite' ? t('home:upcomingEvents.onsite') : t('home:upcomingEvents.hybrid')}
                      </span>
                    </div>
                    <h4 className="wellness-banner-title">{evt.title}</h4>
                    {evt.expert_name && (
                      <p className="wellness-banner-expert">
                        <FontAwesomeIcon icon={faUserDoctor} /> {evt.expert_name}
                      </p>
                    )}
                    <div className="wellness-banner-card-bottom">
                      <span className="wellness-banner-category">{evt.category.replace(/_/g, ' ')}</span>
                      {evt.is_registered ? (
                        <span className="wellness-banner-registered">{t('home:upcomingEvents.registered')}</span>
                      ) : isFull ? (
                        <span className="wellness-banner-full">{t('home:upcomingEvents.eventFull')}</span>
                      ) : (
                        <span className="wellness-banner-register">{t('home:upcomingEvents.registerNow')}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      )} */}

      {/* ============ ANNOUNCEMENTS ============ */}
      {!announcementsLoading && myAnnouncements.length > 0 && (
        <section className="home-announcements-section">
          <Container>
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  <FontAwesomeIcon icon={faBullhorn} className="section-title-icon" />
                  {t('home:announcements.title')}
                </h2>
                <p className="section-subtitle">{t('home:announcements.subtitle')}</p>
              </div>
              {myAnnouncements.length > 4 && (
                <button
                  type="button"
                  className="section-header-link"
                  onClick={() => navigate('/announcements')}
                >
                  {t('common:buttons.viewAll')} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
            </div>
            <div className="announcements-grid">
              {myAnnouncements.slice(0, 4).map((ann) => {
                const pConfig = announcementPriorityConfig[ann.priority] || announcementPriorityConfig.normal;
                return (
                  <AnnouncementCard
                    key={ann.id}
                    ann={ann}
                    pConfig={pConfig}
                    expanded={false}
                    onToggleExpand={() => {}}
                    markingReadId={markingReadId}
                    onMarkRead={handleMarkAsRead}
                    onOpen={() => navigate(`/announcements?id=${ann.id}`)}
                  />
                );
              })}
            </div>
          </Container>
        </section>
      )}

      {/* ============ REMINDERS / TASKS / TICKETS ============ */}
      <section className="home-events-section">
        <Container>
          <div className="section-header">
            <div>
              <h2 className="section-title">
                <FontAwesomeIcon icon={faCalendarCheck} className="section-title-icon" />
                {t('home:activity.title')}
              </h2>
              <p className="section-subtitle">{t('home:activity.subtitle')}</p>
            </div>
          </div>

          <div className="home-activity-grid">
            {/* ── Reminders Card ── */}
            <div className="home-activity-card">
              <div className="home-activity-card-header" style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)' }}>
                <div className="home-activity-card-icon">
                  <FontAwesomeIcon icon={faBell} />
                </div>
                <div className="home-activity-card-info">
                  <span className="home-activity-card-title">{t('home:reminders.title')}</span>
                  <span className="home-activity-card-count">{t('home:activity.upcoming', { count: activeReminders.length })}</span>
                </div>
                <button className="home-activity-card-link" onClick={() => navigate('/reminders?tab=upcoming')}>
                  {t('common:buttons.viewAll')} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              <div className="home-activity-card-body">
                {remindersLoading ? (
                  <div className="home-activity-loading"><FontAwesomeIcon icon={faSpinner} spin /> {t('common:labels.loading')}</div>
                ) : activeReminders.length === 0 ? (
                  <div className="home-activity-empty">
                    <FontAwesomeIcon icon={faBell} />
                    <span>{t('home:reminders.noReminders')}</span>
                  </div>
                ) : (
                  <div className="home-activity-list">
                    {activeReminders.slice(0, 10).map((r) => {
                      const config = categoryConfig[r.category] || categoryConfig.other;
                      return (
                        <div className="home-activity-item" key={r.id} onClick={() => navigate(`/reminders?tab=${getReminderTab(r)}`)}>
                          <div className="home-activity-item-icon" style={{ background: `${config.color}12`, color: config.color }}>
                            <FontAwesomeIcon icon={config.icon} />
                          </div>
                          <div className="home-activity-item-content">
                            <span className="home-activity-item-title">{r.title}</span>
                            <span className="home-activity-item-meta">{formatReminderDate(r.reminder_datetime)}</span>
                          </div>
                          <span className="home-activity-item-badge" style={{ background: `${priorityColors[r.priority] || '#64748b'}14`, color: priorityColors[r.priority] || '#64748b' }}>
                            {r.priority}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Tasks Card ── */}
            <div className="home-activity-card">
              <div className="home-activity-card-header" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <div className="home-activity-card-icon">
                  <FontAwesomeIcon icon={faListCheck} />
                </div>
                <div className="home-activity-card-info">
                  <span className="home-activity-card-title">{t('home:activity.tasks')}</span>
                  <span className="home-activity-card-count">{t('home:activity.active', { count: tasks.length })}</span>
                </div>
                <button className="home-activity-card-link" onClick={() => navigate('/reminders?tab=tasks')}>
                  {t('common:buttons.viewAll')} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              <div className="home-activity-card-body">
                {tasksLoading ? (
                  <div className="home-activity-loading"><FontAwesomeIcon icon={faSpinner} spin /> {t('common:labels.loading')}</div>
                ) : tasks.length === 0 ? (
                  <div className="home-activity-empty">
                    <FontAwesomeIcon icon={faListCheck} />
                    <span>{t('home:activity.noTasks')}</span>
                  </div>
                ) : (
                  <div className="home-activity-list">
                    {tasks.slice(0, 10).map((t) => (
                      <div className="home-activity-item" key={t.id} onClick={() => navigate('/reminders?tab=tasks')}>
                        <div className="home-activity-item-icon" style={{ background: '#eef2ff', color: '#6366f1' }}>
                          <FontAwesomeIcon icon={faListCheck} />
                        </div>
                        <div className="home-activity-item-content">
                          <span className="home-activity-item-title">{t.title}</span>
                          {t.subtask_progress && t.subtask_progress.total > 0 ? (
                            <div className="home-activity-item-progress">
                              <div className="home-activity-progress-bar">
                                <div className="home-activity-progress-fill" style={{ width: `${(t.subtask_progress.completed / t.subtask_progress.total) * 100}%` }} />
                              </div>
                              <span className="home-activity-progress-text">{t.subtask_progress.completed}/{t.subtask_progress.total}</span>
                            </div>
                          ) : (
                            <span className="home-activity-item-meta">{formatReminderDate(t.reminder_datetime)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Support Tickets Card ── */}
            <div className="home-activity-card">
              <div className="home-activity-card-header" style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)' }}>
                <div className="home-activity-card-icon">
                  <FontAwesomeIcon icon={faHeadset} />
                </div>
                <div className="home-activity-card-info">
                  <span className="home-activity-card-title">{t('home:activity.support')}</span>
                  <span className="home-activity-card-count">{t('home:activity.open', { count: supportTickets.length })}</span>
                </div>
                <button className="home-activity-card-link" onClick={() => navigate('/support')}>
                  {t('common:buttons.viewAll')} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              <div className="home-activity-card-body">
                {ticketsLoading ? (
                  <div className="home-activity-loading"><FontAwesomeIcon icon={faSpinner} spin /> {t('common:labels.loading')}</div>
                ) : supportTickets.length === 0 ? (
                  <div className="home-activity-empty">
                    <FontAwesomeIcon icon={faHeadset} />
                    <span>{t('home:activity.noTickets')}</span>
                  </div>
                ) : (
                  <div className="home-activity-list">
                    {supportTickets.slice(0, 10).map((tkt: any) => {
                      const st = statusLabelMap[tkt.status] || { label: tkt.status, color: '#64748b' };
                      return (
                        <div className="home-activity-item" key={tkt.id} onClick={() => navigate(`/support/${tkt.id}`)}>
                          <div className="home-activity-item-icon" style={{ background: `${st.color}12`, color: st.color }}>
                            <FontAwesomeIcon icon={faHeadset} />
                          </div>
                          <div className="home-activity-item-content">
                            <span className="home-activity-item-title">{tkt.case_id}</span>
                            <span className="home-activity-item-meta">{tkt.tags?.[0]?.replace(/_/g, ' ') || 'Support'}</span>
                          </div>
                          <span className="home-activity-item-badge" style={{ background: `${st.color}14`, color: st.color }}>
                            {st.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ HEALTH DISCOUNTS ============*/}
      {canAccess('discount-vouchers') && (
      <section className="home-discounts-section">
        <Container>
          <div className="section-header">
            <div>
              <h2 className="section-title">
                <FontAwesomeIcon icon={faTags} className="section-title-icon" />
                {t('home:discounts.title')}
              </h2>
              <p className="section-subtitle">{t('home:discounts.subtitle')}</p>
            </div>
            <button className="events-view-all-btn" onClick={() => navigate('/offers')}>
              {t('home:discounts.allVouchers')}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          <div className="home-discounts-grid">
            {([
              { type: 'consultation', label: t('home:discounts.consultations'), tagline: t('home:discounts.consultationsTagline'), icon: faUserDoctor, color: '#06b6d4', gradientFrom: '#0e7490', gradientTo: '#22d3ee' },
              { type: 'diagnostics', label: t('home:discounts.diagnostics'), tagline: t('home:discounts.diagnosticsTagline'), icon: faFlask, color: '#8b5cf6', gradientFrom: '#6d28d9', gradientTo: '#a78bfa' },
              { type: 'pharmacy', label: t('home:discounts.pharmacy'), tagline: t('home:discounts.pharmacyTagline'), icon: faCapsules, color: '#10b981', gradientFrom: '#047857', gradientTo: '#34d399' },
              { type: 'wellness', label: t('home:discounts.wellness'), tagline: t('home:discounts.wellnessTagline'), icon: faSpa, color: '#f59e0b', gradientFrom: '#d97706', gradientTo: '#fbbf24' },
              { type: 'general', label: t('home:discounts.general'), tagline: t('home:discounts.generalTagline'), icon: faTags, color: '#e11d48', gradientFrom: '#be123c', gradientTo: '#fb7185' },
            ] as const).map((card, idx) => (
              <div
                className="home-discounts-card"
                key={card.type}
                style={{ background: `linear-gradient(135deg, ${card.gradientFrom}, ${card.gradientTo})`, animationDelay: `${idx * 0.06}s` }}
                onClick={() => navigate(`/discounts/${card.type}`)}
              >
                <div className="home-discounts-card-glow" />
                <div className="home-discounts-card-icon">
                  <FontAwesomeIcon icon={card.icon} />
                </div>
                <h3 className="home-discounts-card-title">{card.label}</h3>
                <p className="home-discounts-card-tagline">{card.tagline}</p>
                <button className="home-discounts-card-btn">
                  {t('home:discounts.explore')}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            ))}
          </div>
        </Container>
      </section>
      )}

      {/* ============ SERVICES SECTION ============ */}
      <section className="home-services-section">
        <Container>
          <div className="services-grid">
            {visibleServices.map((service, idx) => (
              <div
                className="service-card"
                key={idx}
                ref={el => {
                  if (service.serviceSlug === 'elderly-care') {
                    careProgramRef.current = el;
                  }
                }}
              >
                <div className="service-card-img">
                  {service.video ? (
                    <video
                      src={service.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster={service.img}
                      style={{
                        objectPosition: service.videoPosition || 'top center',
                        transformOrigin: service.videoPosition || 'top center',
                        ...(service.videoFit ? { objectFit: service.videoFit } : {}),
                      }}
                    />
                  ) : (
                    <img src={service.img} alt={service.title} />
                  )}
                  {service.comingSoon && (
                    <div className="coming-soon-badge">{t('home:carePrograms.comingSoon')}</div>
                  )}
                </div>
                <div className="service-card-content">
                  <div className="service-card-header">
                    <div className="service-card-icon" style={{ background: `${service.color}12`, color: service.color }}>
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                    <h3 className="service-card-title">{service.title}</h3>
                    {(() => {
                      const assignment = getServiceAssignment(service.serviceSlug);
                      if (!assignment) return null;
                      return (
                        <span className={`service-pricing-badge ${assignment.is_free ? 'free' : 'paid'}`}>
                          {assignment.is_free ? t('home:carePrograms.free') : `₹${assignment.effective_price}`}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="service-card-desc">{service.description}</p>
                  {!service.comingSoon && service.quickActions && service.quickActions.length > 0 && (
                    <div className="service-card-actions">
                      {service.quickActions.map((action, i) => (
                        <button
                          key={i}
                          className="service-quick-btn"
                          style={{ color: service.color, borderColor: `${service.color}40` }}
                          onClick={() => navigate(action.route)}
                        >
                          <FontAwesomeIcon icon={action.icon} />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

    </div>
  );
};

export default Home;
